import React, { useState, useContext, useEffect } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

// components
import CartSummaryDiscountCredit from './CartSummaryDiscountCredit';
import CaretDownIcon from '@icons/CaretDownIcon';
import { BodyMedium, BodySmall, ButtonNoStyles, LinkSmall, LinkText } from '@styled';

// functionality
import { LoadingContext } from '@context/LoadingProvider';
import { updateCartItem } from '@general/api/cartAPI';
import {
    applyEventCredit,
    removeEventCredit,
} from '@general/helpers/cart.helpers';
import colors from '@general/colors';
import fonts from '@general/fonts';
import reactQueryClient from '@general/reactQueryClient';
import useCartFromAPI from '@hooks/apiLegacy/useCartFromAPI';
import useViewer from '@hooks/useViewer';
import productTypes from '@general/productTypes';
import useCartClearFlashNoticesUpdateCart from '@hooks/useCartClearFlashNoticesUpdateCart';
import { formatMoney } from '@general/helpers/currency.helpers';

const HeadingStyled = styled(BodyMedium)`
    font-weight: 700;
`;

const PromoCodeInputStyled = styled.input`
    border: 1px solid #dde1e3;
    flex: 1 1 0%;
    font-family: ${fonts.sans};
    font-size: 18px;
    height: 40px;
    letter-spacing: 0.31px;
    line-height: 25px;
    outline: none;
    padding: 8px 14px;

    &::placeholder {
        color: ${colors.mediumGray};
    }
`;

const PromoCodeApplyButtonStyled = styled.button`
    background-color: ${colors.azure};
    border: none;
    color: white;
    font-family: ${fonts.sans};
    font-size: 14px;
    height: 40px;
    letter-spacing: 0.94px;
    line-height: 22px;
    padding: 9px 16px;

    &[disabled] {
        background-color: ${colors.ultraLightGray};
        color: ${colors.mediumGray};
    }
`;

const CartSummaryDiscount = ({ setCartHasErrors }) => {
    // -----------------------------
    // State
    // -----------------------------

    const [isOpen, setIsOpen] = useState(false);
    const [promoCodeInput, setPromoCodeInput] = useState('');
    const [promoCodes, setPromoCodes] = useState([]);
    const { clearFlashNoticesRefetchCart } = useCartClearFlashNoticesUpdateCart();

    // -----------------------------
    // Hooks
    // -----------------------------

    const { isLoading, setIsLoading } = useContext(LoadingContext);
    const { viewer } = useViewer();
    const { cart } = useCartFromAPI({ viewerId: viewer?.recordId });

    // -----------------------------
    // Effects
    // -----------------------------

    useEffect(() => {
        if (!cart) {
            return;
        }

        // Check for flash notices on cart change
        const flashNoticesExist = cart.items.some(item => item.meta_info?.flash_notice);
        setCartHasErrors(flashNoticesExist);

        if (cart._calculated?.uniqueDiscounts) {
            const cartPromoCodes = cart._calculated.uniqueDiscounts.map(discount => {
                return {
                    code: discount.promotional_code,
                    discountDescription: discount.promotional_discount_description,
                    type: discount.type,
                };
            });

            setPromoCodes(cartPromoCodes);
        }
    }, [cart, setCartHasErrors]);

    // -----------------------------
    // Helpers
    // -----------------------------

    const applyPromoCode = async () => {
        setIsLoading(true);
        setCartHasErrors(false);
        const updateActions = [];

        cart.items.forEach(item => {
            updateActions.push(() => {
                const { remove_promo_code: _, ...metaInfo } = item.meta_info;
                return updateCartItem({
                    cartId: cart.id,
                    cartItemId: item.id,
                    cartItemData: {
                        meta_info: {
                            ...metaInfo,
                            promo_code: promoCodeInput,
                        },
                    },
                    viewerId: viewer.recordId,
                });
            });
        });

        // Do this instead of Promise.all because I need to read the response of the last one.
        const updatedCart = await updateActions.reduce(async (previousAction, nextAction) => {
            return previousAction.then(() => nextAction());
        }, Promise.resolve());

        const itemWithDiscountIndex = updatedCart.items.findIndex(item => {
            return !!item.discounts.find(
                discount => discount.promotional_code.toUpperCase() === promoCodeInput.toUpperCase()
            );
        });

        const wasDiscountApplied = itemWithDiscountIndex >= 0;

        if (wasDiscountApplied) {
            reactQueryClient.setQueryData(`cart-${viewer.recordId}`, existingCartData => {
                if (!existingCartData) {
                    return null;
                }

                const newCartData = { ...existingCartData };
                newCartData.items[itemWithDiscountIndex].discounts = [
                    ...updatedCart.items[itemWithDiscountIndex].discounts,
                ];

                newCartData._calculated.discounts = newCartData.items.reduce(
                    (tally, item) => [...tally, ...item.discounts],
                    []
                );

                return newCartData;
            });

            setPromoCodeInput('');
            clearFlashNoticesRefetchCart();
        } else {
            setCartHasErrors(true);
        }

        setIsLoading(false);
    };

    const removePromoCode = async currentPromoCode => {
        const message = () => {
            const promoDescription =
                currentPromoCode.type === 'event_credit'
                    ? 'Event Credit'
                    : currentPromoCode.discountDescription || `${currentPromoCode.code}`;

            return `Are you sure you want to remove ${promoDescription}?`;
        };

        if (!confirm(message())) {
            return;
        }

        setIsLoading(true);

        try {
            if (currentPromoCode.type === 'event_credit') {
                await removeEventCredit({ cart, viewer });
            } else {
                // find cart items that have the discount
                const foundItems = cart.items.filter(
                    item =>
                        item.discounts &&
                        item.discounts.find(discount => discount.promotional_code === currentPromoCode.code)
                );

                foundItems.forEach(async item => {
                    await updateCartItem({
                        cartId: cart.id,
                        cartItemId: item.id,
                        cartItemData: {
                            meta_info: {
                                ...item.meta_info,
                                promo_code: null,
                                remove_promo_code: currentPromoCode.code,
                            },
                        },
                        viewerId: viewer.recordId,
                    });
                });
            }

            setPromoCodeInput('');
            setPromoCodes(promoCodes.filter(promoCode => promoCode.code !== currentPromoCode.code));
        } catch (error) {
            alert('Error removing promo code.');
        } finally {
            setIsLoading(false);
        }
    };

    // -----------------------------
    // Render
    // -----------------------------

    if (!cart) {
        return null;
    }

    const isCartEmpty = cart.items.length === 0;
    const disableApplyDiscount =
        cart.total <= 0 ||
        promoCodeInput.length === 0 ||
        promoCodes.find(promoCode => promoCode.code === promoCodeInput);
    const hasEventItems = cart.items.some(item => item.product_type === productTypes.EVENT);

    return (
        <div>
            <div className="flex justify-between">
                <HeadingStyled>Promo Code and Discounts</HeadingStyled>
                <ButtonNoStyles
                    className="flex items-center"
                    onClick={e => {
                        e.preventDefault();
                        setIsOpen(!isOpen);
                    }}
                >
                    <BodySmall as="span" className="mr-0.5rem text-azure">
                        {!isOpen && <>View</>}
                        {isOpen && <>Hide</>}
                    </BodySmall>
                    <CaretDownIcon color={colors.azure} />
                </ButtonNoStyles>
            </div>

            {isOpen && (
                <div className="pt-1rem">
                    {cart.available_event_credit + cart._calculated.appliedEventCredit > 0 &&
                        !isCartEmpty &&
                        hasEventItems && (
                            <div className="mb-1rem">
                                <CartSummaryDiscountCredit
                                    amountAvailable={cart.available_event_credit}
                                    amountUsed={cart._calculated.appliedEventCredit}
                                    creditType="Event"
                                    locale={cart.locale}
                                    hideUndo
                                    onApply={async () => {
                                        setIsLoading(true);
                                        await applyEventCredit({ cart, viewer });
                                        setIsLoading(false);
                                    }}
                                />
                            </div>
                        )}

                    <div className="flex">
                        <PromoCodeInputStyled
                            placeholder="Enter Code"
                            value={promoCodeInput}
                            onChange={e => setPromoCodeInput(e.target.value.trim())}
                            onKeyDown={e => {
                                if (e.key === 'Enter' && !disableApplyDiscount) {
                                    applyPromoCode();
                                }
                            }}
                        />

                        <PromoCodeApplyButtonStyled disabled={disableApplyDiscount} onClick={() => applyPromoCode()}>
                            Apply
                        </PromoCodeApplyButtonStyled>
                    </div>

                    {promoCodes &&
                        promoCodes.map((currentPromoCode, index) => {
                            let label = currentPromoCode.discountDescription
                                ? currentPromoCode.discountDescription
                                : `Promo Code: ${currentPromoCode.code}`;

                            if (currentPromoCode.type === 'event_credit') {
                                label = 'Event Credit';
                            }

                            return (
                                <div className="mt-0.5rem text-right" key={index}>
                                    <LinkText style={{ color: colors.black, fontStyle: 'italic' }}>{label}</LinkText>
                                    {currentPromoCode.type !== 'prompt' && currentPromoCode.type !== 'auto' && (
                                        <LinkSmall
                                            href="#"
                                            className="ml-0.5rem"
                                            onClick={e => {
                                                e.preventDefault();
                                                removePromoCode(currentPromoCode);
                                            }}
                                        >
                                            <u>Undo</u>
                                        </LinkSmall>
                                    )}
                                </div>
                            );
                        })}
                </div>
            )}
        </div>
    );
};

CartSummaryDiscount.propTypes = {
    setCartHasErrors: PropTypes.func,
};

export default CartSummaryDiscount;
